import { Undo2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApi, errorText } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import CategoryPicker from "@/components/ledger/CategoryPicker";

const REASON = {
  bank: { label: "Added from an earlier bank file", tone: "text-muted-foreground border-border" },
  ledger: { label: "Matches a ledger row (same date & amount)", tone: "text-amber-500 border-amber-500/40" },
  deleted: { label: "You deleted this line before", tone: "text-rose-500 border-rose-500/40" },
};

// Lines held back from the review: see why, fix the matching ledger row, or bring the line back.
function Skipped({ s, categories, onAddBack, onMatchChange }) {
  const api = useApi();
  const r = REASON[s.reason] || REASON.ledger;
  const editMatch = async (patch) => {
    try {
      const t = await api("put", `/transactions/${s.match.id}`, patch);
      onMatchChange(s.ref, { description: t.description, category: t.category });
      toast.success("Ledger row updated");
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <tr className="border-b border-border/60 last:border-0 align-top" data-testid={`bank-skipped-${s.ref}`}>
      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{s.date}</td>
      <td className="px-2 py-3 min-w-[220px]">
        <div>{s.description}</div>
        <span className={`inline-block mt-1 rounded-full border px-2 py-0.5 text-[11px] ${r.tone}`}>{r.label}</span>
      </td>
      <td className="px-2 py-3 text-right font-mono whitespace-nowrap">
        {s.received ? <span className="text-emerald-500">+{formatMoney(s.received)}</span> : <span className="text-rose-500">-{formatMoney(s.spent)}</span>}
      </td>
      <td className="px-2 py-3 min-w-[300px]">
        {s.match ? (
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">In ledger {s.match.fy} · {s.match.date}</div>
            <div className="flex items-center gap-1">
              <Pencil className="h-3 w-3 text-muted-foreground shrink-0" />
              <input defaultValue={s.match.description} className="h-8 flex-1 rounded-md bg-transparent px-2 text-sm border border-border/60 focus:border-primary focus:outline-none"
                onBlur={(e) => e.target.value !== s.match.description && editMatch({ description: e.target.value })} data-testid={`bank-skipped-desc-${s.ref}`} />
            </div>
            <CategoryPicker value={s.match.category} categories={categories} testId={`bank-skipped-cat-${s.ref}`} onChange={(v) => editMatch({ category: v })} />
          </div>
        ) : <span className="text-xs text-muted-foreground">Not in the ledger</span>}
      </td>
      <td className="px-4 py-3 text-right">
        {s.reason !== "bank" && (
          <Button size="sm" variant="outline" className="rounded-full whitespace-nowrap" onClick={() => onAddBack(s)} data-testid={`bank-skipped-add-${s.ref}`}>
            {s.reason === "deleted" ? <><Undo2 className="h-4 w-4 mr-1" />Restore to review</> : <><Plus className="h-4 w-4 mr-1" />Add anyway</>}
          </Button>
        )}
      </td>
    </tr>
  );
}

export default function SkippedPanel({ skipped, categories, onAddBack, onMatchChange }) {
  if (!skipped?.length) {
    return <p className="text-sm text-muted-foreground" data-testid="bank-skipped-empty">Nothing was held back. If this review was saved before this feature, re-upload the CSV to list held-back lines.</p>;
  }
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="bank-skipped-panel">
      <div className="px-4 py-3 border-b border-border text-sm text-muted-foreground">
        These lines were not put in the review. Fix the matching ledger row here, or bring a line back if it really is a separate transaction.
      </div>
      <table className="w-full text-sm">
        <tbody>
          {skipped.map((s) => <Skipped key={s.ref} s={s} categories={categories} onAddBack={onAddBack} onMatchChange={onMatchChange} />)}
        </tbody>
      </table>
    </div>
  );
}
